import React, { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  formatDateTime, useContainerWidth, EmptyState,
  CHART_MARGINS, defaultLegendProps, tickLabelStyle, rotatedTickLabelStyle,
  toggleButtonGroupSx,
} from './shared';

/**
 * Bidding Strategy Chart
 *
 * Visualizes bid stack over time: average MW offered across the 10 NEM
 * price bands per asset. Shows how generators position their capacity —
 * floor-price bidding to ensure dispatch vs higher bands for revenue maximisation.
 */

const BID_BAND_COLORS = [
  '#064e3b', '#047857', '#059669', '#10b981', '#34d399',
  '#fcd34d', '#f59e0b', '#f97316', '#ef4444', '#b91c1c',
];

const BID_BAND_LABELS = [
  'Band 1 (Floor)', 'Band 2', 'Band 3', 'Band 4', 'Band 5',
  'Band 6', 'Band 7', 'Band 8', 'Band 9', 'Band 10 (Cap)',
];

const BiddingStrategyChart = ({ data, assets, frequency }) => {
  const { ref, width } = useContainerWidth();
  const [selectedAsset, setSelectedAsset] = useState(null);

  if (!data || data.length === 0) return <EmptyState message="No bidding data available" />;

  const assetIds = [...new Set(data.map((d) => d.asset_id))];
  const assetLookup = {};
  (assets || []).forEach((a) => { assetLookup[a.asset_id] = a; });

  const activeAsset = selectedAsset || assetIds[0];
  const assetData = data.filter((d) => d.asset_id === activeAsset);
  const sortedData = [...assetData].sort((a, b) => String(a.date_time).localeCompare(String(b.date_time)));

  const xLabels = sortedData.map((d) => formatDateTime(d.date_time, frequency));

  const series = Array.from({ length: 10 }, (_, i) => ({
    label: BID_BAND_LABELS[i],
    data: sortedData.map((d) => d[`bandavail${i + 1}`] || 0),
    stack: 'bids',
    color: BID_BAND_COLORS[i],
  }));

  return (
    <Box>
      {assetIds.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={activeAsset}
            exclusive
            onChange={(_, v) => { if (v) setSelectedAsset(v); }}
            size="small"
            sx={{ ...toggleButtonGroupSx, flexWrap: 'wrap' }}
          >
            {assetIds.map((aid) => (
              <ToggleButton key={aid} value={aid}>{assetLookup[aid]?.name || aid}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
        {width > 0 && (
          <BarChart
            width={width}
            height={420}
            series={series}
            xAxis={[{
              scaleType: 'band',
              data: xLabels,
              tickLabelStyle: rotatedTickLabelStyle,
              tickNumber: xLabels.length > 30 ? 30 : undefined,
            }]}
            yAxis={[{ valueFormatter: (v) => `${v.toFixed(0)} MW`, tickLabelStyle }]}
            margin={CHART_MARGINS}
            slotProps={{
              legend: {
                ...defaultLegendProps,
                labelStyle: { fill: '#5a6a7e', fontSize: 10 },
                itemMarkWidth: 8, itemMarkHeight: 8, markGap: 3, itemGap: 8,
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default BiddingStrategyChart;
